import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';
import { fetchFormBySlug } from '../lib/slugUtils';
import { logService } from '../lib/logService';
import type { Form, CreateFormInput, UpdateFormInput, FormsFilter } from '../types/forms';

// =====================================================
// HOOK PARA CRUD DE FORMULÁRIOS - ONEFORM
// =====================================================

export function useForms(filter?: FormsFilter) {
  const { user } = useAuth();
  const [forms, setForms] = useState<Form[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Buscar formulários
  const fetchForms = useCallback(async () => {
    if (!user) {
      setForms([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('forms')
        .select('*, form_responses(count)')
        .eq('user_id', user.id);

      // Aplicar filtros
      if (filter?.status === 'published') {
        query = query.eq('is_published', true);
      } else if (filter?.status === 'draft') {
        query = query.eq('is_published', false);
      }

      if (filter?.isTemplate !== undefined) {
        query = query.eq('is_template', filter.isTemplate);
      }

      if (filter?.search) {
        query = query.ilike('title', `%${filter.search}%`);
      }

      // Ordenação
      const sortBy = filter?.sortBy || 'updated_at';
      const sortOrder = filter?.sortOrder || 'desc';
      query = query.order(sortBy, { ascending: sortOrder === 'asc' });

      const { data, error: fetchError } = await query;

      if (fetchError) {
        logService.error('forms.fetch', fetchError.message, { supabase_code: fetchError.code });
        setError('Erro ao carregar formulários');
        return;
      }

      // Mapear resposta com contagem
      const mappedForms: Form[] = (data || []).map((form: any) => ({
        ...form,
        response_count: form.form_responses?.[0]?.count || 0,
      }));

      setForms(mappedForms);
    } catch (err) {
      logService.error('forms.fetch', err instanceof Error ? err.message : 'Unknown error', { error: err as Error });
      setError('Erro inesperado ao carregar formulários');
    } finally {
      setLoading(false);
    }
  }, [user?.id, filter?.status, filter?.isTemplate, filter?.search, filter?.sortBy, filter?.sortOrder]);

  // Carregar ao montar ou quando filtros mudarem
  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  // Criar formulário
  const createForm = useCallback(async (input: CreateFormInput): Promise<{ form: Form | null; error: string | null }> => {
    if (!user) {
      return { form: null, error: 'Usuário não autenticado' };
    }

    try {
      const { data, error: createError } = await supabase
        .from('forms')
        .insert({
          user_id: user.id,
          title: input.title,
          description: input.description || '',
          slug: input.slug || null,
          fields: input.fields || [],
          pages: input.pages || null,
          settings: input.settings || {},
          is_template: input.is_template || false,
          is_published: input.is_published || false,
        })
        .select()
        .single();

      if (createError) {
        logService.error('forms.create', createError.message, { supabase_code: createError.code, title: input.title });
        return { form: null, error: 'Erro ao criar formulário' };
      }

      // Atualizar lista local
      setForms(prev => [data as Form, ...prev]);

      return { form: data as Form, error: null };
    } catch (err) {
      logService.error('forms.create', err instanceof Error ? err.message : 'Unknown error', { error: err as Error, title: input.title });
      return { form: null, error: 'Erro inesperado ao criar formulário' };
    }
  }, [user?.id]);

  // Atualizar formulário
  const updateForm = useCallback(async (
    formId: string,
    updates: UpdateFormInput
  ): Promise<{ form: Form | null; error: string | null }> => {
    try {
      const { data, error: updateError } = await supabase
        .from('forms')
        .update(updates)
        .eq('id', formId)
        .select()
        .single();

      if (updateError) {
        logService.error('forms.update', updateError.message, { supabase_code: updateError.code, form_id: formId });
        return { form: null, error: 'Erro ao atualizar formulário' };
      }

      // Atualizar lista local
      setForms(prev => prev.map(f => f.id === formId ? (data as Form) : f));

      return { form: data as Form, error: null };
    } catch (err) {
      logService.error('forms.update', err instanceof Error ? err.message : 'Unknown error', { error: err as Error, form_id: formId });
      return { form: null, error: 'Erro inesperado ao atualizar formulário' };
    }
  }, []);

  // Deletar formulário
  const deleteForm = useCallback(async (formId: string): Promise<{ error: string | null }> => {
    try {
      const { error: deleteError } = await supabase
        .from('forms')
        .delete()
        .eq('id', formId);

      if (deleteError) {
        logService.error('forms.delete', deleteError.message, { supabase_code: deleteError.code, form_id: formId });
        return { error: 'Erro ao deletar formulário' };
      }

      // Atualizar lista local
      setForms(prev => prev.filter(f => f.id !== formId));

      return { error: null };
    } catch (err) {
      logService.error('forms.delete', err instanceof Error ? err.message : 'Unknown error', { error: err as Error, form_id: formId });
      return { error: 'Erro inesperado ao deletar formulário' };
    }
  }, []);

  // Duplicar formulário
  const duplicateForm = useCallback(async (formId: string): Promise<{ form: Form | null; error: string | null }> => {
    const originalForm = forms.find(f => f.id === formId);
    if (!originalForm) {
      return { form: null, error: 'Formulário não encontrado' };
    }

    return createForm({
      title: `${originalForm.title} (cópia)`,
      description: originalForm.description,
      fields: originalForm.fields,
      pages: originalForm.pages,
      settings: originalForm.settings,
      is_template: false,
    });
  }, [forms, createForm]);

  // Publicar/Despublicar formulário
  const togglePublish = useCallback(async (formId: string): Promise<{ error: string | null }> => {
    const form = forms.find(f => f.id === formId);
    if (!form) {
      return { error: 'Formulário não encontrado' };
    }

    const result = await updateForm(formId, { is_published: !form.is_published });
    return { error: result.error };
  }, [forms, updateForm]);

  return {
    forms,
    loading,
    error,
    refetch: fetchForms,
    createForm,
    updateForm,
    deleteForm,
    duplicateForm,
    togglePublish,
  };
}

// =====================================================
// HOOK PARA UM FORMULÁRIO ESPECÍFICO
// =====================================================

export function useForm(formId: string | undefined) {
  const [form, setForm] = useState<Form | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchForm = useCallback(async () => {
    if (!formId) {
      setForm(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('forms')
        .select('*')
        .eq('id', formId)
        .single();

      if (fetchError) {
        logService.error('forms.fetch', fetchError.message, { supabase_code: fetchError.code, form_id: formId });
        setError('Formulário não encontrado');
        return;
      }

      setForm(data as Form);
    } catch (err) {
      logService.error('forms.fetch', err instanceof Error ? err.message : 'Unknown error', { error: err as Error, form_id: formId });
      setError('Erro inesperado ao carregar formulário');
    } finally {
      setLoading(false);
    }
  }, [formId]);

  useEffect(() => {
    fetchForm();
  }, [fetchForm]);

  const updateForm = useCallback(async (updates: UpdateFormInput): Promise<{ error: string | null }> => {
    if (!formId) return { error: 'ID do formulário não fornecido' };

    try {
      const { data, error: updateError } = await supabase
        .from('forms')
        .update(updates)
        .eq('id', formId)
        .select()
        .single();

      if (updateError) {
        logService.error('forms.update', updateError.message, { supabase_code: updateError.code, form_id: formId });
        return { error: 'Erro ao atualizar formulário' };
      }

      if (data) {
        setForm(data as Form);
      }
      return { error: null };
    } catch (err) {
      logService.error('forms.update', err instanceof Error ? err.message : 'Unknown error', { error: err as Error, form_id: formId });
      return { error: 'Erro inesperado ao atualizar' };
    }
  }, [formId]);

  return {
    form,
    loading,
    error,
    refetch: fetchForm,
    updateForm,
  };
}

// =====================================================
// HOOK PARA FORMULÁRIO PÚBLICO (sem autenticação)
// =====================================================

export function usePublicForm(formId: string | undefined) {
  const [form, setForm] = useState<Form | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPublicForm = async () => {
      if (!formId) {
        setForm(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from('forms')
          .select('id, user_id, title, description, fields, pages, settings, thank_you_message, redirect_url')
          .eq('id', formId)
          .eq('is_published', true)
          .single();

        if (fetchError) {
          logService.error('forms.fetch_public', fetchError.message, { supabase_code: fetchError.code, form_id: formId });
          setError('Formulário não encontrado ou não está publicado');
          return;
        }

        // Check if form owner has active subscription or trial.
        // saas-1.24-hotfix4: também busca owner_plan_tier via RPC get_effective_plan (BP-1, SECURITY DEFINER)
        // — sem essa chamada, OneFormBadge fail-closed defensive trata user como 'free' e ignora showBranding,
        // fazendo branding aparecer mesmo quando Pro/Business desabilita.
        if (data.user_id) {
          const [{ data: ownerActive }, { data: ownerPlan }] = await Promise.all([
            supabase.rpc('is_form_owner_active', { form_user_id: data.user_id }),
            supabase.rpc('get_effective_plan', { p_user_id: data.user_id }),
          ]);

          if (ownerActive === false) {
            setError('form_owner_inactive');
            return;
          }

          // Injeta tier no form (campo derivado client-side; tabela forms não persiste)
          (data as Form).owner_plan_tier = (ownerPlan ?? 'free') as Form['owner_plan_tier'];
        }

        setForm(data as Form);
      } catch (err) {
        logService.error('forms.fetch_public', err instanceof Error ? err.message : 'Unknown error', { error: err as Error, form_id: formId });
        setError('Erro ao carregar formulário');
      } finally {
        setLoading(false);
      }
    };

    fetchPublicForm();
  }, [formId]);

  const submitResponse = useCallback(async (
    data: Record<string, any>,
    scores?: Record<string, any> | null
  ): Promise<{ error: string | null }> => {
    if (!formId) return { error: 'ID do formulário não fornecido' };

    try {
      const insertPayload: Record<string, any> = {
        form_id: formId,
        data,
      };

      if (scores !== undefined && scores !== null) {
        insertPayload.scores = scores;
      }

      // Don't use .select().single() — anon role has no SELECT policy on
      // form_responses, so PostgREST would roll back the entire INSERT.
      const { error: submitError } = await supabase
        .from('form_responses')
        .insert(insertPayload);

      if (submitError) {
        logService.error('forms.submit_response', submitError.message, { supabase_code: submitError.code, form_id: formId, data_keys: Object.keys(data) });
        return { error: 'Erro ao enviar resposta. Tente novamente.' };
      }

      return { error: null };
    } catch (err) {
      logService.error('forms.submit_response', err instanceof Error ? err.message : 'Unknown error', { error: err as Error, form_id: formId });
      return { error: 'Erro inesperado ao enviar' };
    }
  }, [formId]);

  return {
    form,
    loading,
    error,
    submitResponse,
  };
}

// =====================================================
// HOOK PARA FORMULÁRIO PÚBLICO VIA USERNAME + SLUG
// =====================================================

export function usePublicFormBySlug(username: string | undefined, slug: string | undefined) {
  const [resolvedFormId, setResolvedFormId] = useState<string | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [resolving, setResolving] = useState(true);

  useEffect(() => {
    if (!username || !slug) {
      setResolveError('Link inválido');
      setResolving(false);
      return;
    }

    const resolve = async () => {
      setResolving(true);
      const { form, error } = await fetchFormBySlug(username, slug);
      if (error || !form) {
        setResolveError(error || 'Formulário não encontrado');
      } else {
        setResolvedFormId(form.id);
      }
      setResolving(false);
    };

    resolve();
  }, [username, slug]);

  const publicForm = usePublicForm(resolvedFormId ?? undefined);

  return {
    ...publicForm,
    loading: resolving || publicForm.loading || (resolvedFormId !== null && !publicForm.form && !publicForm.error),
    error: resolveError || publicForm.error,
  };
}
